import {useState, useEffect} from 'react';
import {useAppStore} from '@/store/appStore';
import {fetchHospitalDetail, submitFeedback} from '@/services/api';
import {formatDateTime, formatNumber, resolveLevel, levelTheme} from '@/utils/format.ts';
import {toProvinceDisplayName} from '@/utils/provinces.ts';
import type {HospitalDetail} from '@/types';
import {toast} from '@/components/common/toast';
import {ImsChart} from "./Charts/ImsChart.tsx";
import {ProductChart} from "./Charts/ProductChart.tsx";
import {Spinner} from "@/components/common/Spinner.tsx";

const POSITIVE_TAGS = ['预警准确', '预警及时', '预警有帮助'];
const NEGATIVE_TAGS = ['预警不及时', '预警夸大事实', '预警低估事实'];
interface Props {
    hospitalCode: string;
    batch: string;
    onClose: () => void;
}


/* ---------- Close icon (X) ---------- */
function CloseButton({onClick}: { onClick: () => void }) {
    return (
        <button
            onClick={onClick}
            style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 4L12 12M12 4L4 12" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        </button>
    );
}

/* ---------- Thanks overlay ---------- */
function ThanksOverlay({onClose}: { onClose: () => void }) {
    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: '#fff', borderRadius: 10, padding: '24px 28px',
                    textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                }}
            >
                <div style={{fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 8}}>感谢评价</div>
                <div style={{fontSize: 12, color: '#64748b', marginBottom: 16}}>您的反馈已提交到管理后台。</div>
                <button
                    onClick={onClose}
                    style={{
                        padding: '6px 18px', borderRadius: 6, border: 'none',
                        background: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    }}
                >确定
                </button>
            </div>
        </div>
    );
}

/* ===================================================================
   Main component
   =================================================================== */

export default function HospitalModal({hospitalCode, batch, onClose}: Props) {
    const [detail, setDetail] = useState<HospitalDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [chartMode, setChartMode] = useState<'vol' | 'val'>('vol');
    const [reviewResult, setReviewResult] = useState<string | null>(null);
    const [feedbackTags, setFeedbackTags] = useState<string[]>([]);
    const [showThanks, setShowThanks] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(batch);
    const availableBatches = useAppStore((s) => s.availableBatches);
    const selectExchange = useAppStore().selectExchange

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setLoadError('');
        fetchHospitalDetail(hospitalCode, selectedBatch)
            .then((data) => {
                if (alive) {
                    setDetail(data);
                    setReviewResult(null);
                    setFeedbackTags([]);
                }
            })
            .catch((err) => {
                if (alive) setLoadError(err instanceof Error ? err.message : 'Failed');
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [hospitalCode, selectedBatch]);

    const handleSubmitFeedback = async () => {
        if (!detail || submitting) return;
        if (!reviewResult) {
            setSubmitError('Please select True or False.');
            return;
        }
        setSubmitError('');
        setSubmitting(true);
        try {
            await submitFeedback({
                hospitalCode: detail.hospital.hospitalCode,
                yearMonth: detail.batch,
                reviewResult,
                feedbackTags,
            });
            toast.success('反馈已成功提交');
            setShowThanks(true);
            const fresh = await fetchHospitalDetail(hospitalCode, selectedBatch);
            setDetail(fresh);
        } catch (err: any) {
            setSubmitError(err?.message || 'Failed');
        } finally {
            setSubmitting(false);
        }
    };

    /* ---- Loading state ---- */
    if (loading) {
        return (
            <div onClick={onClose} style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div onClick={(e) => e.stopPropagation()}
                     style={{background: '#fff', borderRadius: 10, width: 360, padding: 40, textAlign: 'center'}}>
                    <Spinner size={40}/>
                </div>
            </div>
        );
    }

    /* ---- Error state ---- */
    if (loadError || !detail) {
        return (
            <div onClick={onClose} style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div onClick={(e) => e.stopPropagation()} style={{
                    background: '#fff',
                    borderRadius: 10,
                    width: 360,
                    padding: 24,
                    textAlign: 'center',
                    color: '#dc2626'
                }}>
                    {loadError || 'No data'}
                </div>
            </div>
        );
    }

    /* ---- Content ---- */
    const h = detail.hospital;
    const displayLevel = resolveLevel(h.iraiLevel, h.iraiScore);
    const theme = levelTheme(displayLevel);
    const feedbackOptions =
        reviewResult === 'True' ? POSITIVE_TAGS
            : reviewResult === 'False' ? NEGATIVE_TAGS
                : [...POSITIVE_TAGS, ...NEGATIVE_TAGS];
    const iraiActionWidth = 272;
    const existingFeedback = detail.feedback;

    return (
        <div onClick={onClose} style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: '#fff', borderRadius: 10, width: '92%', maxWidth: 960, maxHeight: '92vh', overflow: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}>

                {/* ======== HEADER ======== */}
                <div style={{
                    padding: '16px 24px 12px', borderBottom: '1px solid #e2e8f0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                }}>
                    <div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4}}>
                            <h2 style={{fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0}}>{h.name}</h2>
                            <span style={{
                                padding: '2px 10px',
                                background: '#f1f5f9',
                                borderRadius: 10,
                                fontSize: 11,
                                fontWeight: 600,
                                color: '#64748b'
                            }}>
                {h.segment}
              </span>
                        </div>
                        <div style={{fontSize: 11, color: '#94a3b8'}}>
                            {h.region} | {toProvinceDisplayName(h.province)}
                        </div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <select
                            value={selectedBatch}
                            onChange={(e) => setSelectedBatch(e.target.value)}
                            style={{
                                padding: "4px 8px",
                                fontSize: 12,
                                borderRadius: 6,
                                border: "1px solid #e2e8f0",
                                background: "#eeeeee",
                                color: "#1e293b",
                                cursor: "pointer",
                                fontWeight: 600
                            }}>{availableBatches.map((b) => (
                            <option key={b.id} value={b.id}>{b.year}-{b.month}</option>))}</select>
                        <CloseButton onClick={onClose}/>
                    </div>
                </div>

                {/* ======== MIDDLE: IRAI + IMS Trend ======== */}
                <div style={{display: 'flex', gap: 0, borderBottom: '1px solid #e2e8f0'}}>

                    {/* ---- LEFT: IRAI ---- */}
                    <div style={{
                        width: 404, padding: '18px 28px', borderRight: '1px solid #e2e8f0',
                        display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#1e293b',
                            marginBottom: 10,
                            textAlign: 'center'
                        }}>IRAI
                        </div>

                        {/* Level badge */}
                        <div style={{
                            width: iraiActionWidth, alignSelf: 'center',
                            background: theme.bg, borderRadius: 10, padding: '24px 0',
                            textAlign: 'center', marginBottom: 12,
                        }}>
                            <span style={{fontSize: 28, fontWeight: 700, color: theme.fg}}>{displayLevel}</span>
                        </div>

                        {/* Score */}
                        <div style={{fontSize: 11, color: '#64748b', textAlign: 'center', marginBottom: 10}}>
                            Score: {Number(h.iraiScore).toFixed(2)}
                        </div>

                        {/* AI disclaimer */}
                        <div style={{fontSize: 9, color: '#94a3b8', textAlign: 'center', marginBottom: 12}}>
                            Generated by AI, for reference only
                        </div>

                        {/* Existing feedback */}
                        {existingFeedback && (
                            <div style={{
                                width: iraiActionWidth, alignSelf: 'center',
                                padding: 8, background: '#f8fafc', borderRadius: 6,
                                fontSize: 11, color: '#64748b', marginBottom: 12,
                            }}>
                                <div>
                                    Review:{' '}
                                    <span style={{
                                        fontWeight: 600,
                                        color: existingFeedback.reviewResult === 'True' ? '#22c55e' : '#dc2626'
                                    }}>
                    {existingFeedback.reviewResult}
                  </span>
                                </div>
                                <div>Tags: {existingFeedback.feedbackTags.join(', ') || '--'}</div>
                                <div>By: {existingFeedback.submittedBy || '--'} at {formatDateTime(existingFeedback.submittedAt)}</div>
                            </div>
                        )}

                        {/* Submit error */}
                        {submitError && (
                            <div style={{
                                width: iraiActionWidth,
                                alignSelf: 'center',
                                color: '#dc2626',
                                fontSize: 11,
                                marginBottom: 12,
                                textAlign: 'center'
                            }}>
                                {submitError}
                            </div>
                        )}

                        {/* True / False toggle */}
                        <div style={{
                            width: iraiActionWidth,
                            alignSelf: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 12
                        }}>
                            {(['True', 'False'] as const).map((val) => (
                                <div key={val} onClick={() => setReviewResult(val)}
                                     style={{display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer'}}>
                  <span style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: reviewResult === val ? (val === 'True' ? '#22c55e' : '#dc2626') : '#d1d5db',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700,
                  }}>
                    {val === 'True' ? '✓' : '×'}
                  </span>
                                    <span style={{
                                        fontSize: 12, fontWeight: 600,
                                        color: reviewResult === val ? (val === 'True' ? '#166534' : '#991b1b') : '#94a3b8',
                                    }}>
                    {val}
                  </span>
                                </div>
                            ))}
                        </div>

                        {/* Feedback tags */}
                        {reviewResult && (
                            <div style={{
                                width: iraiActionWidth,
                                alignSelf: 'center',
                                display: 'flex',
                                gap: 8,
                                flexWrap: 'nowrap',
                                marginBottom: 12
                            }}>
                                {feedbackOptions.slice(0, 3).map((tag) => {
                                    const active = feedbackTags.includes(tag);
                                    return (
                                        <span
                                            key={tag}
                                            onClick={() => setFeedbackTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag])}
                                            style={{
                                                flex: 1, minWidth: 0, textAlign: 'center', padding: '4px 6px',
                                                fontSize: 10, borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap',
                                                background: active ? (reviewResult === 'True' ? '#ecfdf5' : '#fef2f2') : '#fff',
                                                color: active ? (reviewResult === 'True' ? '#166534' : '#991b1b') : '#94a3b8',
                                                border: `1px solid ${active ? (reviewResult === 'True' ? '#86efac' : '#fca5a5') : '#e2e8f0'}`,
                                            }}
                                        >{tag}</span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Submit button */}
                        <button
                            onClick={handleSubmitFeedback}
                            disabled={!reviewResult}
                            style={{
                                alignSelf: 'center', padding: '6px 18px', borderRadius: 6, border: 'none',
                                background: reviewResult ? '#dc2626' : '#cbd5e1', color: '#fff',
                                fontSize: 11, fontWeight: 700, cursor: reviewResult ? 'pointer' : 'not-allowed',
                            }}>
                            提交
                        </button>
                    </div>

                    {/* ---- RIGHT: IMS Trend ---- */}
                    <div style={{
                        flex: '0 0 500px',
                        minWidth: 0,
                        padding: '12px 16px',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 4
                        }}>
                            <div style={{fontSize: 13, fontWeight: 700, color: '#1e293b'}}>IMS Trend</div>
                            <div style={{fontSize: 10, color: '#94a3b8', fontWeight: 600}}>(IMS/K,{selectExchange?.show_char})</div>
                        </div>
                        {detail.trend.length ? (
                            // <div style={{flex: 1, minHeight: 260}}><ImsChart trend={detail.trend}/></div>
                            <div style={{flex: 1, minHeight: 260}}><ImsChart trend={detail.trend}/></div>
                        ) : (
                            <div style={{
                                flex: 1,
                                minHeight: 260,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <div style={{color: '#94a3b8', fontSize: 12}}>No trend data</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ======== BOTTOM: Product Analysis ======== */}
                <div style={{padding: '12px 24px 16px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 4
                    }}>
                        <span style={{fontSize: 13, fontWeight: 700, color: '#1e293b'}}>Product Analysis (L5)</span>
                        <div style={{display: 'flex', gap: 0}}>
                            <button
                                onClick={() => setChartMode('vol')}
                                style={{
                                    padding: '3px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                    borderRadius: '6px 0 0 6px', border: '1px solid #e2e8f0',
                                    background: chartMode === 'vol' ? '#1e293b' : '#fff',
                                    color: chartMode === 'vol' ? '#fff' : '#64748b',
                                }}
                            >Vol
                            </button>
                            <button
                                onClick={() => setChartMode('val')}
                                style={{
                                    padding: '3px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                    borderRadius: '0 6px 6px 0', border: '1px solid #e2e8f0', borderLeft: 'none',
                                    background: chartMode === 'val' ? '#1e293b' : '#fff',
                                    color: chartMode === 'val' ? '#fff' : '#64748b',
                                }}
                            >Val
                            </button>
                        </div>
                    </div>
                    {detail.productMetrics.length ? (
                        <div style={{height: 220}}>
                            <ProductChart metrics={detail.productMetrics} chartMode={chartMode}/>
                        </div>
                    ) : (
                        <div style={{height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <div style={{color: '#94a3b8', fontSize: 12}}>No product data</div>
                        </div>
                    )}
                </div>
            </div>

            {showThanks && <ThanksOverlay onClose={() => setShowThanks(false)}/>}
        </div>
    );
}
